import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatInOrgTz, formatDateTime, relativeDue } from '../lib/datetime';
import NextEventModal from '../components/NextEventModal';
import EditConsultantControl, { CONSULTANT_EDITABLE_STATUSES } from '../components/EditConsultantControl';
import CompleteTaskModal from '../components/CompleteTaskModal';
import { Link, useParams, useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import VehicleInterestModal from '../components/VehicleInterestModal';
import MarkDeadLeadModal from '../components/MarkDeadLeadModal';
import StageChangeModal from '../components/StageChangeModal';
import BookAppointmentModal from '../components/BookAppointmentModal';
import RescheduleModal from '../components/RescheduleModal';
import TestDriveCheckInModal from '../components/testdrive/TestDriveCheckInModal';
import CompleteTestDriveModal from '../components/CompleteTestDriveModal';
import AssignConsultant from '../components/AssignConsultant';
import DealQuotesPanel from '../components/DealQuotesPanel';
import { nextBestAction, actionColor } from '../lib/nextAction';
import { sourceLabel, isRevival, isSystemNote } from '../lib/leadSource';
import { useCrm, LEAD_STAGES, STAGE_META, isClosedStage, customerFromLead } from '../lib/crm';
import { useToast } from '../lib/toast';
import {
  leadsService, outboundService, alertsService, appointmentsService, tasksService,
} from '../lib/services';
import { useMutation } from '../lib/hooks';
import { apiErrorMessage } from '../lib/apiError';
import { useVehicleMap } from '../lib/useVehicleMap';
import { useConsultantOptions } from '../lib/useDirectory';
import OwnershipChip from '../components/OwnershipChip';

// Backend Interaction.channel enum → our outbound service method.
// The very first inbound message we receive is also our best signal for
// which channel to reply on (WhatsApp customers want WhatsApp back, SMS
// customers want SMS back). Email is a sensible fallback.
// The outbound endpoints refuse with 409 and a machine reason; say what it means
// instead of "Request failed with status code 409".
const SEND_REFUSAL_REASONS = {
  CONSENT_REVOKED: 'the customer has opted out, or has no consent on record for this channel',
  SIMULATED_CUSTOMER: 'this is a test customer, so nothing is sent',
  OUT_OF_WINDOW: 'outside the permitted sending hours',
  DNC_BLOCKED: 'the number is on the do-not-call list',
  DNC_UNCERTAIN: 'the do-not-call status could not be checked',
};
function sendFailureReason(err) {
  const d = err?.response?.data;
  if (d?.message) return d.message;
  if (d?.reason) return SEND_REFUSAL_REASONS[d.reason] || d.reason;
  return apiErrorMessage(err);
}

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function dispatchByChannel(channel, payload) {
  const c = String(channel || '').toUpperCase();
  if (c === 'WHATSAPP') return outboundService.whatsapp(payload);
  if (c === 'SMS')      return outboundService.sms(payload);
  if (c === 'EMAIL') {
    // The email endpoint takes email / subject / bodyHtml, not a phone number.
    return outboundService.email({
      leadId:     payload.leadId,
      customerId: payload.customerId,
      actor:      payload.actor,
      email:      payload.email,
      subject:    payload.subject || 'Re: your enquiry',
      bodyHtml:   `<p>${escapeHtml(payload.body).replace(/\n/g, '<br>')}</p>`,
    });
  }
  // Default — when in doubt, prefer WhatsApp (it's the primary channel
  // for SpectraIQ dealerships) over SMS, which requires Twilio creds.
  return outboundService.whatsapp(payload);
}

/* ─── formatting helpers ─── */
const fmtDateTime = (iso) => formatInOrgTz(iso, {
  weekday: 'short', day: '2-digit', month: 'short',
  hour: '2-digit', minute: '2-digit', hour12: false,
});

const fmtDate = (iso) => formatInOrgTz(iso, { day: '2-digit', month: 'short', year: 'numeric' });

function relAge(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const mins = Math.floor(Math.max(0, Date.now() - then) / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

const APPT_STATUS_PILL = {
  REQUESTED:   'warning',
  CONFIRMED:   'success',
  CHECKED_IN:  'info',
  COMPLETED:   'success',
  NO_SHOW:     'danger',
  CANCELLED:   'danger',
  RESCHEDULED: 'accent',
};

// Which status transitions the backend actually allows from here
// (AppointmentService.ALLOWED_TRANSITIONS). Anything not listed would 400.
const NEXT_STATUSES = {
  REQUESTED:   [['CONFIRMED', 'Confirm'], ['CANCELLED', 'Cancel']],
  CONFIRMED:   [['CHECKED_IN', 'Check in'], ['NO_SHOW', 'No-show'], ['CANCELLED', 'Cancel']],
  CHECKED_IN:  [['COMPLETED', 'Complete']],
  NO_SHOW:     [['CONFIRMED', 'Re-confirm']],
  RESCHEDULED: [['CONFIRMED', 'Confirm'], ['CANCELLED', 'Cancel']],
  COMPLETED:   [],
  CANCELLED:   [],
};

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { leads, getCustomer, moveLead, refreshLeads } = useCrm();
  const { toast } = useToast();
  const [draft, setDraft] = useState('');

  // Lead state — polled every 3s for assistedBy changes (consultant could
  // hand back from another tab; ai-agent could auto-resume).
  const [liveLead, setLiveLead] = useState(null);

  // Interaction timeline — polled every 3s, augmented by optimistic
  // pending bubbles added the moment Send is clicked.
  const [liveTurns, setLiveTurns] = useState(null);
  const [pendingTurns, setPendingTurns] = useState([]);

  // Appointments booked against this lead.
  const [appointments, setAppointments] = useState([]);

  // Name resolution for the linked stock vehicle + appointment consultants.
  const { vehiclesById } = useVehicleMap();
  const { options: consultantOptions } = useConsultantOptions();
  const consultantNameOf = useMemo(() => {
    const m = new Map(consultantOptions.map(o => [o.id, o.primary]));
    return (id) => (id ? m.get(id) || `Consultant ${String(id).slice(0, 8)}` : null);
  }, [consultantOptions]);

  // Best-effort display label for the car on a test-drive appointment, for
  // the check-in modal header. Prefer the appointment's own stock link, then
  // any label it carries, then the lead's vehicle-of-interest.
  const vehicleLabelForAppt = useCallback((appt) => {
    const v = appt?.vehicleId ? vehiclesById.get(appt.vehicleId) : null;
    if (v) return [v.year, v.make, v.model, v.trim].filter(Boolean).join(' ');
    if (appt?.vehicleLabel) return appt.vehicleLabel;
    return [liveLead?.vehicleMakeRequested, liveLead?.vehicleModelRequested]
      .filter(Boolean).join(' ') || null;
  }, [vehiclesById, liveLead]);

  // Modals
  const [vehInterestOpen, setVehInterestOpen] = useState(false);
  const [markDeadOpen, setMarkDeadOpen]       = useState(false);
  const [stageOpen, setStageOpen]             = useState(false);
  const [bookOpen, setBookOpen]               = useState(false);
  const [rescheduleFor, setRescheduleFor]     = useState(null);
  // The test-drive appointment whose check-in flow (licence, signature,
  // insurance, keys) is open. Same modal the Appointments and Test Drives
  // pages use — the lead-detail panel used to just flip the status, so a
  // check-in from here skipped every compliance capture.
  const [checkinAppt, setCheckinAppt]         = useState(null);
  const [completeDriveAppt, setCompleteDriveAppt] = useState(null);
  const [nextEventOpen, setNextEventOpen]     = useState(false);
  const [completeOpen, setCompleteOpen]       = useState(false);

  // The persisted next event: the earliest open task on this lead. Replaces
  // the old recomputed-every-render "hero", which had no owner, no due date
  // and no id — a suggestion dressed up as a commitment.
  const [nextEvent, setNextEvent] = useState(null);
  const refreshNextEvent = useCallback(async () => {
    if (!id) return;
    try {
      const rows = await tasksService.list({ leadId: id, status: 'OPEN,IN_PROGRESS', page: 0, limit: 50 });
      const list = (Array.isArray(rows) ? rows : (rows?.content ?? []))
        .filter((t) => t.status === 'OPEN' || t.status === 'IN_PROGRESS')
        .sort((a, b) => new Date(a.dueAt || 8.64e15) - new Date(b.dueAt || 8.64e15));
      setNextEvent(list[0] || null);
    } catch (_) { /* keep previous */ }
  }, [id]);
  useEffect(() => { refreshNextEvent(); }, [refreshNextEvent]);

  const pollRef = useRef(null);

  const refreshLead = useCallback(async () => {
    if (!id) return;
    try {
      const lead = await leadsService.get(id);
      setLiveLead(lead?.data ?? lead);
    } catch (_) { /* keep previous */ }
  }, [id]);

  const refreshTurns = useCallback(async () => {
    if (!id) return;
    try {
      const turns = await leadsService.interactions(id);
      setLiveTurns(Array.isArray(turns) ? turns : []);
      // Prune optimistic bubbles that have been confirmed by the server
      // (best-effort string match on body + ~30s window).
      setPendingTurns(prev => prev.filter(p =>
        !(turns || []).some(t =>
          t.direction === 'OUTBOUND'
          && t.body === p.body
          && Math.abs(new Date(t.occurredAt).getTime() - p.occurredAt) < 30000
        )
      ));
    } catch (_) { /* keep previous */ }
  }, [id]);

  const refreshAppointments = useCallback(async () => {
    if (!id) return;
    try {
      const page = await appointmentsService.list({ leadId: id, page: 0, size: 50 });
      const rows = page?.content ?? (Array.isArray(page) ? page : []);
      setAppointments(rows);
    } catch (_) { /* keep previous */ }
  }, [id]);

  // First load + 3-second poll while the page is open.
  useEffect(() => {
    if (!id) return;
    refreshLead();
    refreshTurns();
    refreshAppointments();
    alertsService.readAllForLead(id).catch(() => {});
    pollRef.current = setInterval(() => {
      refreshLead();
      refreshTurns();
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [id, refreshLead, refreshTurns, refreshAppointments]);

  const sendReply = useMutation(({ channel, to, email, body, customerId }) =>
    dispatchByChannel(channel, {
      phoneE164: to,
      email,
      body,
      leadId:     id,
      customerId: customerId || null,
      actor:      'consultant',
    }));

  const conversation = useMemo(() => {
    const real = Array.isArray(liveTurns) ? liveTurns : [];
    const merged = [
      ...real,
      ...pendingTurns.map(p => ({
        id:         p.id,
        direction:  'OUTBOUND',
        actor:      'human',
        body:       p.body,
        channel:    p.channel,
        occurredAt: new Date(p.occurredAt).toISOString(),
        pending:    true,
      })),
    ];
    // API returns newest-first; show oldest at top, newest at bottom (chat order).
    merged.sort((x, y) => new Date(x.occurredAt) - new Date(y.occurredAt));
    // Labels show time-only within a day; when the day changes between messages
    // we prefix the date, so a multi-day thread reads chronologically instead of
    // looking jumbled (e.g. "17:38" then "14:03" across different days).
    let prevDay = null;
    return merged.map(t => {
      const dayKey = formatInOrgTz(t.occurredAt, { day: 'numeric', month: 'short' });
      const time   = formatInOrgTz(t.occurredAt, { hour: '2-digit', minute: '2-digit', hour12: false });
      const label  = dayKey !== prevDay ? `${dayKey} · ${time}` : time;
      prevDay = dayKey;
      return [
        t.direction === 'OUTBOUND' || t.actor === 'AI' ? 'ai' : 'cust',
        label,
        t.body || t.transcript || t.bodyText || t.summary || '(no message content)',
        t.pending ? 'pending' : '',
      ];
    });
  }, [liveTurns, pendingTurns]);

  // Keep the newest message in view. Only follow if the user is already near
  // the bottom, so the 3s poll doesn't yank them away from older messages.
  const convScrollRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const onConvScroll = (e) => {
    const el = e.currentTarget;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };
  useEffect(() => {
    const el = convScrollRef.current;
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [conversation]);

  const lead = leads.find(l => l.id === id);

  // Merge the polled server row over the cached kanban row — the polled one
  // is fresher and carries the full entity.
  const merged = useMemo(() => {
    if (!lead) return null;
    if (!liveLead) return lead;
    return {
      ...lead,
      stage: LEAD_STAGES.includes(liveLead.stage) ? liveLead.stage : lead.stage,
      score: liveLead.score ?? lead.score,
      notes: liveLead.notes ?? lead.notes,
      source: liveLead.source ?? lead.source,
      assignedConsultantId: liveLead.assignedConsultantId ?? lead.assignedConsultantId,
      vehicleOfInterestId:  liveLead.vehicleOfInterestId  ?? lead.vehicleOfInterestId,
      discoveryComplete:    liveLead.discoveryComplete    ?? lead.discoveryComplete,
      discoveryFlags:       liveLead.discoveryFlags       ?? lead.discoveryFlags,
      lostReasonCode:       liveLead.lostReasonCode       ?? lead.lostReasonCode,
      lostNotes:            liveLead.lostNotes            ?? lead.lostNotes,
      stageEnteredAt:       liveLead.stageEnteredAt       ?? lead.stageEnteredAt,
      lastActivityAt:       liveLead.lastActivityAt       ?? lead.lastActivityAt,
      closedAt:             liveLead.closedAt             ?? lead.closedAt,
      sourceReference:      liveLead.sourceReference      ?? lead.sourceReference,
    };
  }, [lead, liveLead]);

  // Prefer the full customer record; until it loads (or if it is outside the cached
  // page of customers) fall back to the name/phone the lead itself carries.
  const fullCustomer = getCustomer(lead?.customerId);
  const customer = useMemo(
    () => (fullCustomer && !fullCustomer.partial
      ? { ...fullCustomer, name: lead?.customerName || fullCustomer.name, phone: fullCustomer.phone || lead?.customerPhone || '' }
      : fullCustomer ?? customerFromLead(lead)),
    [fullCustomer, lead],
  );

  const activeChannel = useMemo(() => {
    const turns = Array.isArray(liveTurns) ? liveTurns : [];
    for (let i = turns.length - 1; i >= 0; i--) {
      const c = (turns[i].channel || '').toUpperCase();
      if (c) return c;
    }
    const src = (liveLead?.source || lead?.source || '').toUpperCase();
    if (src === 'WHATSAPP' || src === 'SMS' || src === 'EMAIL') return src;
    return 'WHATSAPP';
  }, [liveTurns, liveLead, lead]);

  /* ─── actions (declared before the early return guard would be unsafe, so
         we keep the guard below and rely on `merged` being null-checked) ─── */

  const onSend = async () => {
    const text = draft.trim();
    if (!text) return;
    const isEmail = activeChannel === 'EMAIL';
    const phone = customer?.phone;
    const email = customer?.email;
    if (isEmail ? !email : (!phone || phone === '—')) {
      toast(isEmail ? 'No email address on file for this customer'
                    : 'No phone number on file for this customer', { variant: 'danger' });
      return;
    }
    const recipient = isEmail ? email : phone;

    const tempId = `pending-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const optimistic = { id: tempId, body: text, channel: activeChannel, occurredAt: Date.now() };
    stickToBottomRef.current = true;
    setPendingTurns(prev => [...prev, optimistic]);
    setDraft('');

    try {
      await sendReply.mutate({
        channel: activeChannel, to: phone, email, body: text, customerId: customer?.id,
      });
      const tag = activeChannel === 'WHATSAPP' ? 'WhatsApp'
                : activeChannel === 'SMS'      ? 'SMS'
                : activeChannel === 'EMAIL'    ? 'Email'
                                               : activeChannel;
      toast(`Sent ${tag} to ${customer?.name || recipient}: "${text.slice(0, 24)}…"`, { variant: 'success' });
      refreshTurns();
    } catch (err) {
      setPendingTurns(prev => prev.filter(p => p.id !== tempId));
      setDraft(text);
      toast(`Send failed (${activeChannel}): ${sendFailureReason(err)}`, { variant: 'danger' });
    }
  };

  const onSaveVehicleInterest = async (payload) => {
    const saved = await leadsService.updateVehicleInterest(id, payload);
    setLiveLead(prev => (prev ? { ...prev, ...saved } : saved));
    refreshLeads?.();
    toast('Vehicle interest updated', { variant: 'success' });
  };

  // Stage change — routed through the modal so LOST always carries a reason.
  const onChangeStage = async (to, opts) => {
    await moveLead(id, to, opts);
    await refreshLead();
    toast(`Moved to ${STAGE_META[to]?.label || to}`, {
      variant: to === 'LOST' ? 'warning' : 'success',
    });
  };

  // Quick inline stage step — used by the "next stage" shortcut button.
  const onQuickAdvance = async (to) => {
    try {
      await moveLead(id, to, {});
      await refreshLead();
      toast(`Moved to ${STAGE_META[to]?.label || to}`, { variant: 'success' });
    } catch (err) {
      toast(apiErrorMessage(err, 'Could not change the stage'), { variant: 'danger' });
    }
  };

  const isPaused = (liveLead?.assistedBy || 'AI') === 'HUMAN';

  // Who is replying — AI or a consultant — shown as a chip in the top bar. It
  // follows `assistedBy` alone; who OWNS the lead is the Assigned consultant
  // panel's job. (E5 / T045.)
  const ownerLead = {
    assistedBy: liveLead?.assistedBy ?? lead?.assistedBy,
    assignedConsultantId: liveLead?.assignedConsultantId ?? lead?.assignedConsultantId,
  };

  /* ─── next best action (compact — reply is implicit on this page) ─── */
  const newQuote = () => {
    const p = new URLSearchParams({ leadId: lead.id });
    if (merged.customerId || lead.customerId) p.set('customerId', merged.customerId || lead.customerId);
    if (merged.vehicleOfInterestId) p.set('vehicleId', merged.vehicleOfInterestId);
    navigate(`/quotes/new?${p.toString()}`);
  };
  const hasOpenAppt = appointments.some((a) => !['CANCELLED', 'COMPLETED'].includes(a.status));
  // Guarded: `merged` is null until the CRM context has the lead, which is the
  // normal state on a cold load of /leads/:id (a deep link from a dashboard
  // exception, an email, or a refresh). The "Lead not found" guard below runs
  // after this line, so reading merged.stage here crashed the page.
  const heroRaw = merged ? nextBestAction({
    stage: merged.stage,
    unread: false,
    hasConsultant: !!merged.assignedConsultantId,
    quotes: [],
    sale: null,
    hasOpenAppt,
  }) : null;
  // Now a SUGGESTION, shown only when there is no committed next event.
  const hero = heroRaw && heroRaw.key !== 'reply' ? heroRaw : null;   // reply is redundant here
  const runHero = (key) => {
    if (key === 'quote') newQuote();
    else if (key === 'book') setBookOpen(true);
    else if (key === 'assign') document.getElementById('assign-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const onToggleAssist = async () => {
    try {
      if (isPaused) {
        setLiveLead(prev => (prev ? { ...prev, assistedBy: 'AI' } : prev));
        await leadsService.handBackToAI(id);
        refreshLeads?.();
        toast('AI resumed on this conversation', { variant: 'info' });
      } else {
        setLiveLead(prev => (prev ? { ...prev, assistedBy: 'HUMAN' } : prev));
        await leadsService.takeover(id);
        refreshLeads?.();
        toast('AI paused — you’re handling this conversation now', { variant: 'warning' });
      }
    } catch (err) {
      refreshLead();
      toast(`Couldn’t change assist mode: ${apiErrorMessage(err)}`, { variant: 'danger' });
    }
  };

  const onApptStatus = async (appt, status) => {
    // Completing a TEST DRIVE needs the odometer-in reading captured on the
    // drive record (same as the Test Drives module). Hand off to the modal
    // instead of silently flipping just the appointment status.
    if (status === 'COMPLETED' && appt.type === 'TEST_DRIVE') {
      setCompleteDriveAppt(appt);
      return;
    }
    try {
      // COMPLETED requires an outcomeCode — the backend rejects it otherwise.
      const body = { status };
      if (status === 'COMPLETED') body.outcomeCode = 'ATTENDED';
      if (status === 'CANCELLED') body.reason = 'Cancelled by dealership';
      await appointmentsService.updateStatus(appt.id, body);
      await refreshAppointments();
      toast(`Appointment ${status.toLowerCase().replace('_', ' ')}`, { variant: 'success' });
    } catch (err) {
      toast(apiErrorMessage(err, 'Could not update the appointment'), { variant: 'danger' });
    }
  };

  if (!lead || !merged) {
    return (
      <>
        <TopBar crumbs={[['Lead Accelerator', '/leads'], 'Not found']} />
        <div className="app-content">
          <div className="empty-state">
            <div className="icon">?</div>
            <div className="title">Lead not found</div>
            <button className="btn primary small" style={{ marginTop: 14 }} onClick={() => navigate('/leads')}>
              ← Back to board
            </button>
          </div>
        </div>
      </>
    );
  }

  const stage    = merged.stage;
  const meta     = STAGE_META[stage] || {};
  const closed   = isClosedStage(stage);
  const stageIdx = LEAD_STAGES.indexOf(stage);
  // Next stage in the linear journey (skips LOST, which is never "next").
  const nextStage = (!closed && stageIdx >= 0 && stageIdx < LEAD_STAGES.indexOf('DELIVERED'))
    ? LEAD_STAGES[stageIdx + 1]
    : null;

  const upcoming = appointments
    .filter(a => !['CANCELLED', 'COMPLETED'].includes(a.status) && new Date(a.startsAt) >= new Date())
    .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
  const past = appointments
    .filter(a => !upcoming.includes(a))
    .sort((a, b) => new Date(b.startsAt) - new Date(a.startsAt));

  const discovery = merged.discoveryFlags || {};
  const discoveryKeys = Object.keys(discovery);

  return (
    <>
      <TopBar
        crumbs={[['Lead Accelerator', '/leads'], 'Board', customer?.displayName || lead.id]}
        right={
          <>
            <span className="pill filled" style={{ background: 'var(--bg-glass)', color: meta.color }}>
              {meta.label || stage}
            </span>
            <OwnershipChip lead={ownerLead} />
            {nextStage && (
              <button
                className="btn small"
                onClick={() => onQuickAdvance(nextStage)}
                title={`Advance to ${STAGE_META[nextStage]?.label}`}
              >
                → {STAGE_META[nextStage]?.label}
              </button>
            )}
            <button className="btn small" onClick={() => setStageOpen(true)}>Change stage</button>
            <button className="btn small primary" onClick={() => setBookOpen(true)}>📅 Book</button>
            {isPaused ? (
              <button className="btn small" onClick={onToggleAssist} title="The AI is paused — click to resume. Handing back restarts the nurture window.">
                ↺ Hand back to AI
              </button>
            ) : (
              <button
                className="btn small"
                onClick={onToggleAssist}
                title="Pause the AI and reply manually."
              >
                ✋ Take over from AI
              </button>
            )}
          </>
        }
      />
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* ── Left — lead summary ── */}
        <div style={{ width: 288, borderRight: '1px solid var(--border-color)', padding: 16, display: 'flex', flexDirection: 'column', gap: 14, overflow: 'auto' }}>
          <Link
            to={customer ? `/customers/${customer.id}` : '/customers'}
            className="avatar-pill"
            style={{ width: 64, height: 64, fontSize: 18, textDecoration: 'none' }}
          >
            {customer?.initials || '··'}
          </Link>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, fontStyle: customer?.hasRealName ? undefined : 'italic', color: customer?.hasRealName ? undefined : 'var(--text-muted)' }}>{customer?.displayName || '—'}</div>
            <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>
              {customer?.phone || '—'} · {customer?.consents?.sms ? '✓ consent' : '✗ consent'}
            </div>
            <div className="mono text-xs text-muted">{customer?.email || '—'}</div>
            {customer && (
              <Link to={`/customers/${customer.id}`} className="btn small" style={{ marginTop: 8, width: '100%', justifyContent: 'center' }}>
                Open customer profile →
              </Link>
            )}
          </div>

          {/* Assist state — high-signal banner */}
          <div style={{
            padding: '8px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
            background: isPaused ? 'rgba(245,158,11,0.12)' : 'rgba(16,185,129,0.10)',
            color: isPaused ? 'var(--warning)' : 'var(--success)',
          }}>
            <span>{isPaused ? '✋ You’re handling this — AI paused' : '🤖 AI is handling this conversation'}</span>
            <button type="button" className="btn small" style={{ padding: '1px 8px', fontSize: 11 }} onClick={onToggleAssist}>
              {isPaused ? 'Hand back' : 'Take over'}
            </button>
          </div>

          {/* Next event — persisted, owned, dated. */}
          <div className="card" style={{ padding: 12 }}>
            <div className="flex-between" style={{ marginBottom: 6 }}>
              <span className="card-label">NEXT EVENT</span>
              <button type="button" className="btn small" onClick={() => setNextEventOpen(true)}>
                {nextEvent ? '✎ Change' : '+ Set'}
              </button>
            </div>
            {nextEvent ? (
              <>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{nextEvent.title}</div>
                <div className="mono text-xs text-muted" style={{ marginTop: 8, lineHeight: 1.7 }}>
                  {nextEvent.reasonLabel && <>Reason: {nextEvent.reasonLabel}<br /></>}
                  Due: <span style={{
                    color: relativeDue(nextEvent.dueAt).overdue ? 'var(--danger)' : 'var(--text-secondary)',
                    fontWeight: relativeDue(nextEvent.dueAt).overdue ? 600 : 400,
                  }}>
                    {formatDateTime(nextEvent.dueAt)}{relativeDue(nextEvent.dueAt).overdue ? ' · overdue' : ''}
                  </span><br />
                  Owner: {nextEvent.assigneeName || consultantNameOf(nextEvent.assigneeId) || 'unassigned'}
                </div>
                <div className="flex-row" style={{ gap: 8, marginTop: 10 }}>
                  <button type="button" className="btn small primary" onClick={() => setCompleteOpen(true)}>✓ Mark complete</button>
                </div>
              </>
            ) : (
              <>
                {!isClosedStage(merged.stage) && (
                  <div className="mono text-xs" style={{ color: 'var(--warning)', marginBottom: 6 }}>
                    No next step set — every open lead should have one.
                  </div>
                )}
                {hero && (
                  <button
                    type="button"
                    onClick={() => runHero(hero.key)}
                    style={{
                      textAlign: 'left', cursor: 'pointer', padding: '8px 10px', borderRadius: 8, width: '100%',
                      border: `1px solid ${actionColor(hero.tone)}`, background: 'transparent',
                      display: 'flex', flexDirection: 'column', gap: 2,
                    }}
                  >
                    <span className="card-label" style={{ color: actionColor(hero.tone) }}>SUGGESTED: {hero.label}</span>
                    <span className="mono text-xs text-muted">{hero.hint}</span>
                  </button>
                )}
              </>
            )}
          </div>

          {/* Lead score */}
          <div className="card" style={{ padding: 12 }}>
            <div className="card-label">LEAD SCORE</div>
            <div style={{ fontSize: 36, fontWeight: 800, lineHeight: 1, color: merged.score >= 80 ? 'var(--success)' : merged.score < 40 ? 'var(--danger)' : 'var(--warning)' }}>
              {merged.score} <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>/ 100</span>
            </div>
            <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>
              {(customer?.tags || []).slice(0, 3).join(' · ') || 'no tags'}
            </div>
          </div>

          {/* Stage */}
          <div className="card" style={{ padding: 12 }}>
            <div className="flex-between" style={{ marginBottom: 6 }}>
              <span className="card-label">STAGE</span>
              <button type="button" className="btn small" onClick={() => setStageOpen(true)}>✎ Change</button>
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: meta.color }}>{meta.label || stage}</div>

            {/* Journey progress — 9 forward stages, LOST sits outside the track. */}
            <div style={{ display: 'flex', gap: 2, marginTop: 8 }}>
              {LEAD_STAGES.filter(s => s !== 'LOST').map((s, i) => {
                const done = !closed && stageIdx >= 0 && i <= stageIdx;
                return (
                  <span
                    key={s}
                    title={STAGE_META[s]?.label}
                    style={{
                      flex: 1, height: 4, borderRadius: 2,
                      background: stage === 'LOST' ? 'var(--danger-bg)'
                        : done ? (meta.color || 'var(--accent-primary)')
                        : 'var(--border-color)',
                    }}
                  />
                );
              })}
            </div>

            <div className="mono text-xs text-muted" style={{ marginTop: 8, lineHeight: 1.7 }}>
              In stage: {relAge(merged.stageEnteredAt)}<br />
              Last activity: {relAge(merged.lastActivityAt)} ago<br />
              Created: {fmtDate(merged.createdAt)}
              {merged.closedAt && <><br />Closed: {fmtDate(merged.closedAt)}</>}
            </div>

            {stage === 'LOST' && merged.lostReasonCode && (
              <div className="mono text-xs" style={{ marginTop: 8, color: 'var(--danger)', lineHeight: 1.6 }}>
                Lost: {merged.lostReasonCode}
                {merged.lostNotes && <><br /><span className="text-muted">{merged.lostNotes}</span></>}
              </div>
            )}
          </div>

          {/* Assigned consultant */}
          <div className="card" id="assign-card" style={{ padding: 12 }}>
            <div className="flex-between" style={{ marginBottom: 8 }}>
              <span className="card-label">ASSIGNED CONSULTANT</span>
              <span className="mono text-xs text-muted">{consultantNameOf(merged.assignedConsultantId) || 'Unassigned'}</span>
            </div>
            <AssignConsultant
              leadId={lead.id}
              assignedConsultantId={merged.assignedConsultantId}
              onChanged={(u) => setLiveLead((prev) => ({ ...(prev || {}), ...u }))}
            />
          </div>

          {/* Quotes & sale — bridge into the Deal Desk */}
          <DealQuotesPanel
            leadId={lead.id}
            customerId={merged.customerId || lead.customerId}
            vehicleId={merged.vehicleOfInterestId}
            compact
          />

          {/* Vehicle interest */}
          <div className="card" style={{ padding: 12 }}>
            <div className="flex-between" style={{ marginBottom: 4 }}>
              <span className="card-label">VEHICLE INTEREST</span>
              <button type="button" className="btn small" onClick={() => setVehInterestOpen(true)} title="Edit vehicle interest">
                ✎ Edit
              </button>
            </div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              {[liveLead?.vehicleMakeRequested, liveLead?.vehicleModelRequested]
                .filter(Boolean).join(' ') || merged.vehicle || '—'}
            </div>
            {merged.vehicleOfInterestId && (() => {
              const v = vehiclesById.get(merged.vehicleOfInterestId);
              const label = v
                ? [v.year, v.make, v.model, v.trim].filter(Boolean).join(' ')
                : null;
              return (
                <Link
                  to="/inventory"
                  className="mono text-xs"
                  style={{ marginTop: 4, display: 'inline-block', color: 'var(--accent-primary-hover)' }}
                >
                  {label
                    ? `Linked stock: ${label}${v.dmsStockId ? ` · ${v.dmsStockId}` : ''} →`
                    : 'Linked to a stock vehicle →'}
                </Link>
              );
            })()}
            <div className="mono text-xs text-muted" style={{ marginTop: 6, lineHeight: 1.7 }}>
              Source: {sourceLabel(merged.source)}<br />
              {isRevival(merged.source)
                ? <>Origin: revived from a lost-lead campaign{merged.sourceReference ? <> ({merged.sourceReference})</> : null}<br /></>
                : (merged.sourceReference && <>Ref: {merged.sourceReference}<br /></>)}
              Replying on: {activeChannel}<br />
              Assist: {isPaused ? 'Human' : 'AI'}<br />
              Age: {relAge(merged.createdAt)}
            </div>
          </div>

          {/* Discovery */}
          <div className="card" style={{ padding: 12 }}>
            <div className="flex-between">
              <span className="card-label">DISCOVERY</span>
              <span className={`pill ${merged.discoveryComplete ? 'success' : 'warning'} filled`}>
                {merged.discoveryComplete ? 'Complete' : 'In progress'}
              </span>
            </div>
            {discoveryKeys.length === 0 ? (
              <div className="mono text-xs text-muted" style={{ marginTop: 6 }}>
                Nothing captured yet.
              </div>
            ) : (
              <div className="mono text-xs text-muted" style={{ marginTop: 6, lineHeight: 1.8 }}>
                {discoveryKeys.map(k => (
                  <div key={k} className="flex-between">
                    <span>{k.replace(/_/g, ' ')}</span>
                    <span style={{ color: discovery[k] ? 'var(--success)' : 'var(--text-tertiary)' }}>
                      {discovery[k] ? '✓' : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Center — conversation ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="mono text-xs text-muted">REPLYING ON:</span>
            <span className="pill active filled">
              {activeChannel === 'WHATSAPP' ? 'WhatsApp'
               : activeChannel === 'SMS'    ? 'SMS'
               : activeChannel === 'EMAIL'  ? 'Email'
                                            : activeChannel}
            </span>
            {isPaused && (
              <span className="pill warning filled" title="AI auto-reply is paused on this conversation">
                ⏸ AI paused — you’re handling this
              </span>
            )}
            <span style={{ marginLeft: 'auto' }} className="mono text-xs text-muted">
              {conversation.length} turn{conversation.length === 1 ? '' : 's'}
            </span>
          </div>

          <div ref={convScrollRef} onScroll={onConvScroll} style={{ flex: 1, padding: 16, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {conversation.length === 0 && (
              <div className="empty-state" style={{ padding: 32 }}>
                <div className="title">No messages yet</div>
                <div className="desc">Nothing has been exchanged with this customer.</div>
              </div>
            )}
            {conversation.map(([s, t, msg, pending], i) => (
              <div key={i} style={{ display: 'flex', justifyContent: s === 'ai' ? 'flex-start' : 'flex-end' }}>
                <div
                  className={`chat-bubble ${s === 'ai' ? 'ai' : 'customer'}`}
                  style={pending ? { opacity: 0.55 } : (!msg || msg === '(no message content)') ? { opacity: 0.5, fontStyle: 'italic' } : undefined}
                >
                  {msg || '(no message content)'}
                  <div className="chat-meta">
                    {s === 'ai' ? 'AI · ' : '· '}{t}{pending ? ' · sending…' : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ padding: 12, borderTop: '1px solid var(--border-color)', display: 'flex', gap: 8 }}>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onSend(); }}
              placeholder={`Type a ${
                activeChannel === 'WHATSAPP' ? 'WhatsApp'
                : activeChannel === 'SMS'    ? 'SMS'
                : activeChannel === 'EMAIL'  ? 'Email'
                                             : ''
              } reply…`}
              style={{
                flex: 1, padding: '10px 14px',
                background: 'var(--bg-glass)', border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13,
                outline: 'none',
              }}
            />
            <button className="btn small primary" onClick={onSend} disabled={!draft.trim()}>Send</button>
          </div>
        </div>

        {/* ── Right — appointments + actions ── */}
        <div style={{ width: 292, borderLeft: '1px solid var(--border-color)', padding: 16, display: 'flex', flexDirection: 'column', gap: 14, overflow: 'auto' }}>

          {/* Appointments */}
          <div className="card" style={{ padding: 12 }}>
            <div className="flex-between" style={{ marginBottom: 8 }}>
              <span className="card-label">APPOINTMENTS</span>
              <button type="button" className="btn small primary" onClick={() => setBookOpen(true)}>+ Book</button>
            </div>

            {appointments.length === 0 && (
              <div className="mono text-xs text-muted" style={{ lineHeight: 1.6 }}>
                No appointments yet. Book a test drive or showroom visit for this lead.
              </div>
            )}

            {upcoming.map(a => (
              <ApptRow
                key={a.id}
                appt={a}
                consultantName={consultantNameOf(a.consultantId)}
                onReschedule={() => setRescheduleFor(a)}
                onStatus={(s) => onApptStatus(a, s)}
                onCheckIn={() => setCheckinAppt(a)}
                onConsultantChanged={refreshAppointments}
              />
            ))}

            {past.length > 0 && (
              <>
                <div className="mono text-xs text-muted" style={{ margin: '10px 0 4px', letterSpacing: 0.6 }}>
                  PAST
                </div>
                {past.map(a => (
                  <ApptRow
                    key={a.id}
                    appt={a}
                    muted
                    consultantName={consultantNameOf(a.consultantId)}
                    onReschedule={() => setRescheduleFor(a)}
                    onStatus={(s) => onApptStatus(a, s)}
                    onCheckIn={() => setCheckinAppt(a)}
                    onConsultantChanged={refreshAppointments}
                  />
                ))}
              </>
            )}
          </div>

          {/* Actions */}
          <div className="card" style={{ padding: 12 }}>
            <div className="card-label">ACTIONS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button className="btn small" onClick={() => setStageOpen(true)}>⇄ Change stage</button>
              <button className="btn small" onClick={() => setBookOpen(true)}>📅 Book appointment</button>
              <button className="btn small" onClick={() => setVehInterestOpen(true)}>🚗 Edit vehicle interest</button>
              {!closed && (
                <button className="btn small danger" onClick={() => setMarkDeadOpen(true)}>Mark as lost</button>
              )}
              {stage === 'LOST' && (
                <button
                  className="btn small"
                  onClick={async () => {
                    try {
                      await leadsService.reopen(id);
                      await refreshLead();
                      refreshLeads?.();
                      toast('Lead reopened', { variant: 'success' });
                    } catch (err) {
                      toast(apiErrorMessage(err, 'Could not reopen the lead'), { variant: 'danger' });
                    }
                  }}
                >
                  ↺ Reopen lead
                </button>
              )}
            </div>
          </div>

          {/* Notes — genuine consultant notes only; the dead-lead system note
              is surfaced as the "Origin" line above, so we don't repeat it. */}
          {merged.notes && !isSystemNote(merged.notes) && (
            <div className="card" style={{ padding: 12 }}>
              <div className="card-label">NOTES</div>
              <div className="text-xs" style={{ lineHeight: 1.7, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                {merged.notes}
              </div>
            </div>
          )}
        </div>
      </div>

      <VehicleInterestModal
        open={vehInterestOpen}
        lead={liveLead}
        onClose={() => setVehInterestOpen(false)}
        onSave={onSaveVehicleInterest}
      />

      <StageChangeModal
        open={stageOpen}
        lead={merged}
        onClose={() => setStageOpen(false)}
        onChange={onChangeStage}
      />

      <NextEventModal
        open={nextEventOpen}
        leadId={id}
        customerId={merged.customerId || lead.customerId || null}
        task={nextEvent}
        defaultAssigneeId={nextEvent?.assigneeId || merged.assignedConsultantId || null}
        prefill={!nextEvent && hero ? { title: hero.label } : null}
        onClose={() => setNextEventOpen(false)}
        onSaved={() => { setNextEventOpen(false); refreshNextEvent(); }}
      />
      <CompleteTaskModal
        open={completeOpen && !!nextEvent}
        task={nextEvent}
        lead={merged}
        convertsToAppointment={!!nextEvent?.convertsToAppointment}
        onClose={() => setCompleteOpen(false)}
        onCompleted={() => { setCompleteOpen(false); refreshNextEvent(); refreshAppointments(); refreshLead(); }}
      />

      <BookAppointmentModal
        open={bookOpen}
        lead={merged}
        customer={customer}
        onClose={() => setBookOpen(false)}
        onBooked={() => {
          refreshAppointments();
          toast('Appointment booked', { variant: 'success' });
        }}
      />

      {checkinAppt && (
        <TestDriveCheckInModal
          appointmentId={checkinAppt.id}
          scheduledAt={checkinAppt.startsAt}
          customerName={customer?.name || checkinAppt.customerName}
          vehicleLabel={vehicleLabelForAppt(checkinAppt)}
          onClose={() => setCheckinAppt(null)}
          onDone={() => {
            setCheckinAppt(null);
            refreshAppointments();
            refreshLead();
            toast('Test drive started', { variant: 'success' });
          }}
        />
      )}

      {completeDriveAppt && (
        <CompleteTestDriveModal
          appointment={completeDriveAppt}
          leadId={id}
          onClose={() => setCompleteDriveAppt(null)}
          onCompleted={() => {
            setCompleteDriveAppt(null);
            refreshAppointments();
            refreshLead();
            toast('Test drive completed', { variant: 'success' });
          }}
        />
      )}

      <RescheduleModal
        open={!!rescheduleFor}
        appointment={rescheduleFor}
        onClose={() => setRescheduleFor(null)}
        onRescheduled={() => {
          refreshAppointments();
          toast('Appointment rescheduled', { variant: 'success' });
        }}
      />

      <MarkDeadLeadModal
        open={markDeadOpen}
        mode="lead"
        leadId={id}
        onClose={() => setMarkDeadOpen(false)}
        onMarked={() => {
          toast('Lead marked as dead — moved to the lost-lead pool', { variant: 'success' });
          refreshLeads?.();
          navigate('/dead-leads');
        }}
      />
    </>
  );
}

/* ─── One appointment row, with its legal next transitions ─── */
function ApptRow({ appt, muted, consultantName, onReschedule, onStatus, onCheckIn, onConsultantChanged }) {
  const isTestDrive = appt.type === 'TEST_DRIVE';
  // A test drive is checked in through the full licence/signature/keys modal,
  // not the plain status flip — so drop the generic "Check in" transition for
  // test drives and offer "Start test drive" instead (mirrors the Appointments
  // and Test Drives pages). Non-test-drive appointments keep the simple flip.
  // An AI-booked appointment was already confirmed to the customer in chat, so
  // Confirm/Cancel make no sense while it is REQUESTED — one "OK" acknowledges
  // it (REQUESTED → CONFIRMED). Cancel is still available once it is CONFIRMED.
  const aiAwaitingOk = appt.source === 'AI' && appt.status === 'REQUESTED';
  const next = (aiAwaitingOk ? [['CONFIRMED', 'OK']] : (NEXT_STATUSES[appt.status] || []))
    .filter(([status]) => !(isTestDrive && status === 'CHECKED_IN'));
  const canReschedule = !['COMPLETED', 'CANCELLED', 'CHECKED_IN'].includes(appt.status);
  // CHECKED_IN means the drive is already started (keys out, test_drive row is
  // IN_PROGRESS) — the next action is Complete, not Start again. So exclude it
  // alongside the terminal states.
  const canStartDrive = isTestDrive && !['COMPLETED', 'CANCELLED', 'CHECKED_IN'].includes(appt.status);

  return (
    <div
      style={{
        padding: '8px 0',
        borderTop: '1px solid var(--border-subtle)',
      }}
    >
      {/* Only the informational part of a PAST row is dimmed — dimming the
          whole row (opacity on the wrapper) also washed out the action
          buttons, so a live "Start test drive" button read as disabled.
          CSS opacity is multiplicative and can't be undone on a child, so
          the muting is scoped to the header + meta here, not the buttons. */}
      <div style={{ opacity: muted ? 0.62 : 1 }}>
        <div className="flex-between" style={{ gap: 6 }}>
          <span className="mono text-xs" style={{ fontWeight: 600 }}>
            {String(appt.type || '').replace(/_/g, ' ')}
          </span>
          <span className={`pill ${APPT_STATUS_PILL[appt.status] || 'accent'} filled`}>
            {appt.status}
          </span>
        </div>

        <div className="mono text-xs text-muted" style={{ marginTop: 4, lineHeight: 1.6 }}>
          {fmtDateTime(appt.startsAt)}
          {appt.location && <><br />{appt.location}</>}
          {consultantName && <><br />{consultantName}</>}
        </div>
      </div>

      {(canReschedule || canStartDrive || next.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
          {CONSULTANT_EDITABLE_STATUSES.includes(appt.status) && (
            <EditConsultantControl
              appointmentId={appt.id}
              consultantId={appt.consultantId}
              onChanged={onConsultantChanged}
            />
          )}
          {canStartDrive && (
            <button className="btn small primary" onClick={onCheckIn}>🔑 Start test drive</button>
          )}
          {canReschedule && (
            <button className="btn small" onClick={onReschedule}>📅 Reschedule</button>
          )}
          {next.map(([status, label]) => (
            <button
              key={status}
              className={`btn small ${['CANCELLED', 'NO_SHOW'].includes(status) ? 'danger' : ''}`}
              onClick={() => onStatus(status)}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
